import { ArrowLeft20, CheckmarkOutline16, Edit16 } from '@carbon/icons-react';
import moment from 'moment';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { CustomerPortalButtonType } from 'api/customer-portal/theme-api';
import {
  CpBody00Secondary,
  CpBody01Secondary,
  CpBody02Secondary,
  CpHeading04Primary,
} from 'components/atoms/customer-portal/typography';
import { useIsResponsive } from 'hooks/use-media-query';
import { useTaskDrawerQueryParamsFromRouter } from 'hooks/use-query-params';
import { CpButton } from '../../components/cp-button';
import { useGetBackground } from '../../types/utils';
import {
  CpSectionWrapper,
  CpWidgetDescription,
  CpWidgetTitle,
} from '../common';
import { ControlButton } from '../image-gallery/variants/image-gallery-carousel';
import { useCarouselUtils } from '../resources/utils/handlers';
import {
  CarouselControlWrapper,
  ResponsiveCarouselControlsWrapper,
} from '../resources/variants/cp-resources-scrollable-grid';
import { CpProjectForm, CpProjectFormsWidgetProps } from './types';

const StyledWidgetTitle = styled(CpWidgetTitle)`
  margin-bottom: var(--cp-spacing-03);
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: var(--cp-spacing-08);
  border: 1px solid var(--cp-border-subtle);
  border-radius: var(--border-radius-small);
  margin-top: var(--cp-spacing-06);
  background: var(--cp-white);
  overflow: hidden;

  @media (max-width: 480px) {
    flex-direction: column;
    padding: 0%;
  }

  .swiper {
    width: 100%;
    height: 50%;
    margin: 0;
  }

  .swiper-slide {
    text-align: center;
    overflow: hidden;
    /* Center slide text vertically */
    display: flex;
    justify-content: center;
    align-items: center;
    height: auto;
    width: 100%;

    @media (max-width: 768px) {
      width: 100% !important;
    }

    @media (max-width: 480px) {
      width: 100% !important;
    }
  }
`;

const ContentForRight = styled(Content)`
  @media (max-width: 768px) {
    padding: 0%;
  }
`;

const ContentForLeft = styled(Content)`
  padding: 0;
  min-height: 486px;
  height: 486px;
  max-height: 486px;
  .swiper {
    height: 100%;
  }
`;

const WidgetHeadWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: start;
`;

const TitleAndDescWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
  min-height: 230px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ImageWrapperForLeft = styled(ImageWrapper)`
  width: 100%;
  margin-bottom: var(--cp-spacing-05);

  @media (min-width: 768px) {
    margin-bottom: 0;
    img {
      height: 100%;
      width: 100%;
    }
  }
`;

const ImageWrapperForCenter = styled(ImageWrapper)`
  width: 100%;
  margin-bottom: var(--cp-spacing-05);

  img {
    max-height: 200px;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  @media (max-width: 768px) {
    flex-direction: column;

    img {
      width: 100%;
      min-width: 100%;
    }
  }
`;

const FormWrapperForRight = styled(FormWrapper)`
  flex-direction: row;
  width: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    img {
      width: 100%;
      min-width: 100%;
    }
  }

  ${ImageWrapper} {
    width: 100%;
  }
`;

const FormWrapperForCenter = styled(FormWrapper)`
  flex-direction: column;
  width: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FormContentWrapper = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  text-align: left;

  ${CpBody02Secondary} {
    max-width: 70%;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: var(--cp-spacing-05);

    * {
      text-align: left;
    }

    ${CpBody02Secondary} {
      max-width: 850%;
    }
  }

  @media (max-width: 480px) {
    padding: var(--cp-spacing-05);
    margin-top: var(--cp-spacing-05);

    ${CpBody02Secondary} {
      max-width: 100%;
    }
  }
`;

const FormContentWrapperForRight = styled(FormContentWrapper)`
  text-align: left;
  width: 100%;
  margin-right: var(--cp-spacing-08);

  @media (max-width: 768px) {
    margin-right: 0;
    margin-top: var(--cp-spacing-05);
    padding-left: var(--cp-spacing-05);
    padding-right: var(--cp-spacing-05);
  }

  @media (max-width: 480px) {
    margin-right: 0;
  }
`;

const FormContentWrapperForLeft = styled(FormContentWrapper)`
  text-align: left;
  width: 100%;

  margin-top: var(--cp-spacing-05);
  padding-left: var(--cp-spacing-08);
  padding-right: var(--cp-spacing-08);

  @media (min-width: 768px) {
    padding-bottom: var(--cp-spacing-05);
  }

  @media (max-width: 768px) {
    margin-left: var(--cp-spacing-05);
    padding-left: var(--cp-spacing-05);
    padding-right: var(--cp-spacing-05);
  }

  @media (max-width: 480px) {
    margin-left: 0;
  }
`;

const FormContentWrapperForCenter = styled(FormContentWrapper)`
  text-align: left;
  width: 100%;
  padding: var(--cp-spacing-08);
  padding-top: 0;

  @media (max-width: 768px) {
    padding: var(--cp-spacing-06);
  }

  @media (max-width: 480px) {
    padding: var(--cp-spacing-05);
  }
`;

const Image = styled.img`
  /* max-height: 265px; */
  object-fit: cover;
  height: 100%;
  min-height: 230px;
  max-width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ImageForCenter = styled(Image)`
  width: 100%;
  height: unset;
  min-height: unset;
  max-width: unset;
`;

const StyledFormTitle = styled(CpHeading04Primary)`
  margin-bottom: var(--cp-spacing-02);
`;

const DateAndAssignee = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: var(--cp-spacing-05);
`;

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: start;
  width: 100%;
  gap: var(--cp-spacing-05);
  margin-top: var(--cp-spacing-08);
`;

const StyledCarouselControlWrapper = styled(CarouselControlWrapper)`
  width: 50%;
  margin-right: 0;
  position: relative;
`;

const StyledResponsiveCarouselControlsWrapper = styled(
  ResponsiveCarouselControlsWrapper,
)`
  margin-top: var(--cp-spacing-06);
`;

export const SingleFormWithImageAtRight: React.FC<CpProjectFormsWidgetProps> =
  ({ data, widget, disabled, projectId }) => {
    const bg = useGetBackground(widget);
    const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

    const onSubmissionClick = useCallback(
      (form: CpProjectForm) => {
        openTaskDrawer(projectId, form.task.taskId);
      },
      [openTaskDrawer, projectId],
    );

    const isResponsive = useIsResponsive();
    const { handlePrevious, handleNext, setSwiperRef } = useCarouselUtils(
      data.forms?.length ?? 0,
    );

    return (
      <CpSectionWrapper
        background={bg}
        isDisabled={disabled}
        paddingY={
          widget?.widgetDefinition?.styles?.paddingY as number | undefined
        }
      >
        <WidgetHeadWrapper>
          <TitleAndDescWrapper>
            <StyledWidgetTitle autoAdjustColor>{data.title}</StyledWidgetTitle>
            {data?.description ? (
              <CpWidgetDescription autoAdjustColor>
                {data.description}
              </CpWidgetDescription>
            ) : null}
          </TitleAndDescWrapper>
          {!isResponsive && data?.forms?.length > 1 ? (
            <StyledCarouselControlWrapper>
              <ControlButton
                className="controlButton"
                icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
                onClick={handlePrevious}
              />
              <ControlButton
                className="controlButton"
                icon={
                  <ArrowLeft20
                    className="cp-svg-auto-adjust-fill"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                }
                onClick={handleNext}
              />
            </StyledCarouselControlWrapper>
          ) : (
            <></>
          )}
        </WidgetHeadWrapper>
        <ContentForRight>
          <Swiper
            freeMode
            onSwiper={setSwiperRef}
            pagination={{ clickable: true }}
            slidesPerView={1}
            spaceBetween={0}
            modules={[Pagination]}
            className="mySwiper"
          >
            {data?.forms?.map((form) => {
              const formattedDueDate = form?.task?.dueDate
                ? moment(form.task.dueDate).format('MMM DD, YYYY')
                : '';

              return (
                <SwiperSlide key={form.task?.taskId}>
                  <FormWrapperForRight>
                    <FormContentWrapperForRight>
                      <DateAndAssignee>
                        {formattedDueDate ? (
                          <CpBody00Secondary>
                            {formattedDueDate}
                          </CpBody00Secondary>
                        ) : (
                          <></>
                        )}
                      </DateAndAssignee>
                      <StyledFormTitle>{form.task?.taskName}</StyledFormTitle>
                      <CpBody01Secondary>
                        {form.formTemplate?.description}
                      </CpBody01Secondary>
                      <ActionsWrapper>
                        <CpButton
                          onClick={() => onSubmissionClick(form)}
                          cpType={CustomerPortalButtonType.Primary}
                          icon={
                            !form?.hasAnswerBeenSubmitted ? (
                              <Edit16 />
                            ) : (
                              <CheckmarkOutline16 />
                            )
                          }
                          iconPlacement="left"
                        >
                          {form?.hasAnswerBeenSubmitted ? (
                            <FormattedMessage id="projects.form_responses.edit" />
                          ) : (
                            <FormattedMessage id="cp.submit_form" />
                          )}
                        </CpButton>
                      </ActionsWrapper>
                    </FormContentWrapperForRight>
                    <ImageWrapper>
                      <Image
                        src={
                          form?.formTemplate?.formTheme?.headerImage ??
                          'https://s3.us-east-1.amazonaws.com/rocketlane-profile-pic-public/657/45b397c4-1d90-49af-884e-12f1788289d8/Form%20Header-12.png'
                        }
                      />
                    </ImageWrapper>
                  </FormWrapperForRight>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </ContentForRight>
        {isResponsive && data?.forms?.length > 1 ? (
          <StyledResponsiveCarouselControlsWrapper>
            <ControlButton
              className="controlButton"
              icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
              onClick={handlePrevious}
            />
            <ControlButton
              className="controlButton"
              icon={
                <ArrowLeft20
                  className="cp-svg-auto-adjust-fill"
                  style={{ transform: 'scaleX(-1)' }}
                />
              }
              onClick={handleNext}
            />
          </StyledResponsiveCarouselControlsWrapper>
        ) : (
          <></>
        )}
      </CpSectionWrapper>
    );
  };

export const SingleFormWithImageAtLeft: React.FC<CpProjectFormsWidgetProps> = ({
  data,
  widget,
  disabled,
  projectId,
}) => {
  const bg = useGetBackground(widget);

  const isResponsive = useIsResponsive();
  const { handlePrevious, handleNext, setSwiperRef } = useCarouselUtils(
    data.forms?.length ?? 0,
  );

  const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

  const onSubmissionClick = useCallback(
    (form: CpProjectForm) => {
      openTaskDrawer(projectId, form.task.taskId);
    },
    [openTaskDrawer, projectId],
  );

  return (
    <CpSectionWrapper
      background={bg}
      isDisabled={disabled}
      paddingY={
        widget?.widgetDefinition?.styles?.paddingY as number | undefined
      }
    >
      <WidgetHeadWrapper>
        <TitleAndDescWrapper>
          <StyledWidgetTitle autoAdjustColor>{data.title}</StyledWidgetTitle>
          {data?.description ? (
            <CpWidgetDescription autoAdjustColor>
              {data.description}
            </CpWidgetDescription>
          ) : null}
        </TitleAndDescWrapper>
        {!isResponsive && data?.forms?.length > 1 ? (
          <StyledCarouselControlWrapper>
            <ControlButton
              className="controlButton"
              icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
              onClick={handlePrevious}
            />
            <ControlButton
              className="controlButton"
              icon={
                <ArrowLeft20
                  className="cp-svg-auto-adjust-fill"
                  style={{ transform: 'scaleX(-1)' }}
                />
              }
              onClick={handleNext}
            />
          </StyledCarouselControlWrapper>
        ) : (
          <></>
        )}
      </WidgetHeadWrapper>
      <ContentForLeft>
        <Swiper
          freeMode
          onSwiper={setSwiperRef}
          pagination={{ clickable: true }}
          slidesPerView={1}
          spaceBetween={0}
          modules={[Pagination]}
          className="mySwiper"
        >
          {data?.forms?.map((form) => {
            const formattedDueDate = form?.task?.dueDate
              ? moment(form.task.dueDate).format('MMM DD, YYYY')
              : '';

            return (
              <SwiperSlide key={form.task?.taskId}>
                <FormWrapper>
                  <ImageWrapperForLeft>
                    <Image
                      src={
                        form?.formTemplate?.formTheme?.headerImage ??
                        'https://s3.us-east-1.amazonaws.com/rocketlane-profile-pic-public/657/45b397c4-1d90-49af-884e-12f1788289d8/Form%20Header-12.png'
                      }
                    />
                  </ImageWrapperForLeft>
                  <FormContentWrapperForLeft>
                    <DateAndAssignee>
                      {formattedDueDate ? (
                        <CpBody00Secondary>
                          {formattedDueDate}
                        </CpBody00Secondary>
                      ) : (
                        <></>
                      )}
                    </DateAndAssignee>
                    <StyledFormTitle>{form.task?.taskName}</StyledFormTitle>
                    <CpBody01Secondary>
                      {form.formTemplate?.description}
                    </CpBody01Secondary>
                    <ActionsWrapper>
                      <CpButton
                        onClick={() => onSubmissionClick(form)}
                        cpType={CustomerPortalButtonType.Primary}
                        icon={
                          !form?.hasAnswerBeenSubmitted ? (
                            <Edit16 />
                          ) : (
                            <CheckmarkOutline16 />
                          )
                        }
                        iconPlacement="left"
                      >
                        {form?.hasAnswerBeenSubmitted ? (
                          <FormattedMessage id="projects.form_responses.edit" />
                        ) : (
                          <FormattedMessage id="cp.submit_form" />
                        )}
                      </CpButton>
                    </ActionsWrapper>
                  </FormContentWrapperForLeft>
                </FormWrapper>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </ContentForLeft>
      {isResponsive && data?.forms?.length > 1 ? (
        <StyledResponsiveCarouselControlsWrapper>
          <ControlButton
            className="controlButton"
            icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
            onClick={handlePrevious}
          />
          <ControlButton
            className="controlButton"
            icon={
              <ArrowLeft20
                className="cp-svg-auto-adjust-fill"
                style={{ transform: 'scaleX(-1)' }}
              />
            }
            onClick={handleNext}
          />
        </StyledResponsiveCarouselControlsWrapper>
      ) : (
        <></>
      )}
    </CpSectionWrapper>
  );
};

export const SingleFormWithImageAtCenter: React.FC<CpProjectFormsWidgetProps> =
  ({ data, widget, disabled, projectId }) => {
    const bg = useGetBackground(widget);

    const isResponsive = useIsResponsive();
    const { handlePrevious, handleNext, setSwiperRef } = useCarouselUtils(
      data.forms?.length ?? 0,
    );

    const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

    const onSubmissionClick = useCallback(
      (form: CpProjectForm) => {
        openTaskDrawer(projectId, form.task.taskId);
      },
      [openTaskDrawer, projectId],
    );

    return (
      <CpSectionWrapper
        background={bg}
        isDisabled={disabled}
        paddingY={
          widget?.widgetDefinition?.styles?.paddingY as number | undefined
        }
      >
        <WidgetHeadWrapper>
          <TitleAndDescWrapper>
            <StyledWidgetTitle autoAdjustColor>{data.title}</StyledWidgetTitle>
            {data?.description ? (
              <CpWidgetDescription autoAdjustColor>
                {data.description}
              </CpWidgetDescription>
            ) : null}
          </TitleAndDescWrapper>
          {!isResponsive && data?.forms?.length > 1 ? (
            <StyledCarouselControlWrapper>
              <ControlButton
                className="controlButton"
                icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
                onClick={handlePrevious}
              />
              <ControlButton
                className="controlButton"
                icon={
                  <ArrowLeft20
                    className="cp-svg-auto-adjust-fill"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                }
                onClick={handleNext}
              />
            </StyledCarouselControlWrapper>
          ) : (
            <></>
          )}
        </WidgetHeadWrapper>
        <ContentForLeft>
          <Swiper
            freeMode
            onSwiper={setSwiperRef}
            pagination={{ clickable: true }}
            slidesPerView={1}
            spaceBetween={0}
            modules={[Pagination]}
            className="mySwiper"
          >
            {data?.forms?.map((form) => {
              const formattedDueDate = form?.task?.dueDate
                ? moment(form.task.dueDate).format('MMM DD, YYYY')
                : '';

              return (
                <SwiperSlide key={form.task?.taskId}>
                  <FormWrapperForCenter>
                    <ImageWrapperForCenter>
                      <ImageForCenter
                        src={
                          form?.formTemplate?.formTheme?.headerImage ??
                          'https://s3.us-east-1.amazonaws.com/rocketlane-profile-pic-public/657/45b397c4-1d90-49af-884e-12f1788289d8/Form%20Header-12.png'
                        }
                      />
                    </ImageWrapperForCenter>
                    <FormContentWrapperForCenter>
                      <DateAndAssignee>
                        {formattedDueDate ? (
                          <CpBody00Secondary>
                            {formattedDueDate}
                          </CpBody00Secondary>
                        ) : (
                          <></>
                        )}
                      </DateAndAssignee>
                      <StyledFormTitle>{form.task?.taskName}</StyledFormTitle>
                      <CpBody01Secondary>
                        {form.formTemplate?.description}
                      </CpBody01Secondary>
                      <ActionsWrapper>
                        <CpButton
                          onClick={() => onSubmissionClick(form)}
                          cpType={CustomerPortalButtonType.Primary}
                          icon={
                            !form?.hasAnswerBeenSubmitted ? (
                              <Edit16 />
                            ) : (
                              <CheckmarkOutline16 />
                            )
                          }
                          iconPlacement="left"
                        >
                          {form?.hasAnswerBeenSubmitted ? (
                            <FormattedMessage id="projects.form_responses.edit" />
                          ) : (
                            <FormattedMessage id="cp.submit_form" />
                          )}
                        </CpButton>
                      </ActionsWrapper>
                    </FormContentWrapperForCenter>
                  </FormWrapperForCenter>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </ContentForLeft>
        {isResponsive && data?.forms?.length > 1 ? (
          <StyledResponsiveCarouselControlsWrapper>
            <ControlButton
              className="controlButton"
              icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
              onClick={handlePrevious}
            />
            <ControlButton
              className="controlButton"
              icon={
                <ArrowLeft20
                  className="cp-svg-auto-adjust-fill"
                  style={{ transform: 'scaleX(-1)' }}
                />
              }
              onClick={handleNext}
            />
          </StyledResponsiveCarouselControlsWrapper>
        ) : (
          <></>
        )}
      </CpSectionWrapper>
    );
  };
